import { useEffect, useState } from 'react';
import {
  PresenceHolder,
  updateStoryPresence,
} from '../services/storyService';
import { Config } from '../config';

const HEARTBEAT_MS = 45_000;

// Claims a story while its editor/review view is open: claim on mount,
// heartbeat while open, release on unmount. A beacon release covers tab
// close; anything that slips through (crash, closed laptop) expires via
// the backend's 2-minute staleness window. Returns who else currently
// holds the story (null = nobody / it's ours).
export function useStoryPresence(
  storyId: string | undefined,
  email: string | undefined,
  name: string,
  activity: 'editing' | 'reviewing'
): PresenceHolder | null {
  const [heldBy, setHeldBy] = useState<PresenceHolder | null>(null);

  useEffect(() => {
    if (!storyId || !email) return;
    let active = true;

    const claim = async () => {
      const holder = await updateStoryPresence({
        storyId,
        email,
        name,
        action: 'claim',
        activity,
      });
      if (active) setHeldBy(holder);
    };
    claim();
    const interval = setInterval(claim, HEARTBEAT_MS);

    // text/plain keeps the beacon a "simple" CORS request (beacons cannot
    // preflight); the presence endpoint JSON-parses string bodies.
    const beaconRelease = () => {
      navigator.sendBeacon?.(
        Config.endPoint.updateStoryPresence,
        new Blob([JSON.stringify({ storyId, email, action: 'release' })], {
          type: 'text/plain',
        })
      );
    };
    window.addEventListener('pagehide', beaconRelease);

    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('pagehide', beaconRelease);
      updateStoryPresence({ storyId, email, name, action: 'release', activity });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, email]);

  return heldBy;
}
